import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { CrisisTeamBackendService } from 'src/app/services/crisis-team-backend.service';
import { CrisisTeamRole } from '../models/crisis-team-role.model';
import { EmergencyDirectoryContact } from '../models/emergency-directory-contact.model';
import { PersonnelStatus } from '../models/personnel-status.model';
import { TeamDocument } from '../models/team-document.model';
import { UserDropDown } from '../models/user-drop-down.model';
import { Logout } from './auth.action';
import {
	AddDocument,
	DeleteDocument,
	DownloadDocument,
	GetCrisisTeamRoleAssignments,
	GetCrisisTeamRoles,
	GetDocuments,
	GetEmergencyDirectoryContacts,
	GetTravelStatus,
	GetUserDropDowns,
	ResetAllIndicators,
	SendMessage,
	UpdateCrisisTeamRoleAssignments,
	UpdateCrisisTeamRoles,
	UpdateEmergencyDirectoryContacts,
} from './crisis-team.action';
import { CrisisTeamStateModel } from './crisis-team.model';
import { StartLoading, StopLoading } from './main.action';

export const DEFAULT_STATE: CrisisTeamStateModel = {
	roles: null,
	contacts: [],
	documents: null,
	maxUploadSize: null,
	statuses: null,
	userDropDowns: [],
};

@State<CrisisTeamStateModel>({
	name: 'crisisTeam',
	defaults: DEFAULT_STATE,
})
@Injectable()
export class CrisisTeamState {
	private crisisTeamBackend = inject(CrisisTeamBackendService);

	@Selector()
	static crisisTeamState(state: CrisisTeamStateModel): CrisisTeamStateModel {
		return state;
	}

	@Selector()
	static roles(state: CrisisTeamStateModel): CrisisTeamRole[] {
		return state.roles;
	}

	@Selector()
	static contacts(state: CrisisTeamStateModel): EmergencyDirectoryContact[] {
		return state.contacts;
	}

	@Selector()
	static documents(state: CrisisTeamStateModel): TeamDocument[] {
		return state.documents;
	}

	@Selector()
	static maxUploadSize(state: CrisisTeamStateModel): number {
		return state.maxUploadSize;
	}

	@Selector()
	static statuses(state: CrisisTeamStateModel): PersonnelStatus[] {
		return state.statuses;
	}

	@Selector()
	static userDropDowns(state: CrisisTeamStateModel): UserDropDown[] {
		return state.userDropDowns;
	}

	@Selector()
	static userDropDownIds(state: CrisisTeamStateModel): number[] {
		return state.userDropDowns.map((user) => user.id);
	}

	@Action(GetCrisisTeamRoles)
	getCrisisTeamRoles(
		ctx: StateContext<CrisisTeamStateModel>,
		_action: GetCrisisTeamRoles
	) {
		ctx.dispatch([new StartLoading()]);
		return this.crisisTeamBackend.getRoles().pipe(
			tap((roles) => {
				ctx.patchState({ roles: roles });
				ctx.dispatch([new StopLoading()]);
			}),
			catchError((error) => {
				ctx.dispatch([new StopLoading()]);
				return throwError(() => error);
			})
		);
	}

	@Action(UpdateCrisisTeamRoles)
	updateCrisisTeamRoles(
		ctx: StateContext<CrisisTeamStateModel>,
		action: UpdateCrisisTeamRoles
	) {
		ctx.dispatch([new StartLoading()]);
		return this.crisisTeamBackend.updateRoles(action.roles).pipe(
			tap((roles) => {
				ctx.patchState({ roles: roles });
				ctx.dispatch([new StopLoading()]);
			}),
			catchError((error) => {
				ctx.dispatch([new StopLoading()]);
				return throwError(() => error);
			})
		);
	}

	@Action(GetCrisisTeamRoleAssignments)
	getCrisisTeamRoleAssignments(
		ctx: StateContext<CrisisTeamStateModel>,
		action: GetCrisisTeamRoleAssignments
	) {
		ctx.patchState({
			userDropDowns: [],
			roles: null,
		});
		ctx.dispatch([new StartLoading()]);
		return this.crisisTeamBackend.getUserRoles(action.userId).pipe(
			tap((response) => {
				ctx.patchState({
					roles: response.roles.map((role) =>
						CrisisTeamRole.fromJSON(role)
					),
					userDropDowns: response.userDropDowns.map((user) =>
						UserDropDown.fromJSON(user)
					),
				});
				ctx.dispatch([new StopLoading()]);
			}),
			catchError((error) => {
				ctx.dispatch([new StopLoading()]);
				return throwError(() => error);
			})
		);
	}

	@Action(UpdateCrisisTeamRoleAssignments)
	updateCrisisTeamRoleAssignments(
		ctx: StateContext<CrisisTeamStateModel>,
		action: UpdateCrisisTeamRoleAssignments
	) {
		ctx.dispatch([new StartLoading()]);
		return this.crisisTeamBackend
			.updateUserRoles(action.userId, action.roles)
			.pipe(
				tap((_response) => {
					ctx.patchState({
						userDropDowns: [],
						roles: [],
					});
					ctx.dispatch(new StopLoading());
				}),
				catchError((error) => {
					ctx.dispatch(new StopLoading());
					return throwError(() => error);
				})
			);
	}

	@Action(SendMessage)
	sendMessage(ctx: StateContext<CrisisTeamStateModel>, action: SendMessage) {
		ctx.dispatch([new StartLoading()]);
		return this.crisisTeamBackend
			.sendMessage(action.subject, action.message)
			.pipe(
				tap(() => {
					ctx.dispatch([new StopLoading()]);
				}),
				catchError((error) => {
					ctx.dispatch([new StopLoading()]);
					return throwError(() => error);
				})
			);
	}

	@Action(ResetAllIndicators)
	resetAllIndicators(
		ctx: StateContext<CrisisTeamStateModel>,
		_action: ResetAllIndicators
	) {
		ctx.dispatch([new StartLoading()]);
		return this.crisisTeamBackend.resetAllIndicators().pipe(
			tap(() => {
				ctx.dispatch([new StopLoading()]);
			}),
			catchError((error) => {
				ctx.dispatch([new StopLoading()]);
				return throwError(() => error);
			})
		);
	}

	@Action(GetEmergencyDirectoryContacts)
	getEmergencyDirectoryContacts(
		ctx: StateContext<CrisisTeamStateModel>,
		_action: GetEmergencyDirectoryContacts
	) {
		ctx.dispatch([new StartLoading()]);
		return this.crisisTeamBackend.getEmergencyDirectoryContacts().pipe(
			tap((contacts) => {
				ctx.patchState({ contacts: contacts });
				ctx.dispatch([new StopLoading()]);
			}),
			catchError((error) => {
				ctx.dispatch([new StopLoading()]);
				return throwError(() => error);
			})
		);
	}

	@Action(UpdateEmergencyDirectoryContacts)
	updateEmergencyDirectoryContacts(
		ctx: StateContext<CrisisTeamStateModel>,
		action: UpdateEmergencyDirectoryContacts
	) {
		ctx.dispatch([new StartLoading()]);
		return this.crisisTeamBackend
			.updateEmergencyDirectoryContacts(action.contacts)
			.pipe(
				tap((contacts) => {
					ctx.patchState({ contacts: contacts });
					ctx.dispatch([new StopLoading()]);
				}),
				catchError((error) => {
					ctx.dispatch([new StopLoading()]);
					return throwError(() => error);
				})
			);
	}

	@Action(GetDocuments)
	getDocuments(
		ctx: StateContext<CrisisTeamStateModel>,
		_action: GetDocuments
	) {
		ctx.dispatch([new StartLoading()]);
		return this.crisisTeamBackend.getDocuments().pipe(
			tap((response) => {
				ctx.patchState({
					documents: response.documents.map((json) =>
						TeamDocument.fromJSON(json)
					),
					maxUploadSize: response.maxUploadSize,
				});
				ctx.dispatch([new StopLoading()]);
			}),
			catchError((error) => {
				ctx.dispatch([new StopLoading()]);
				return throwError(() => error);
			})
		);
	}

	@Action(DownloadDocument)
	downloadDocument(
		ctx: StateContext<CrisisTeamStateModel>,
		action: DownloadDocument
	) {
		ctx.dispatch([new StartLoading()]);
		return this.crisisTeamBackend.downloadDocument(action.doc).pipe(
			tap((doc) => {
				doc.open();
				ctx.dispatch([new StopLoading()]);
			}),
			catchError((error) => {
				ctx.dispatch([new StopLoading()]);
				return throwError(() => error);
			})
		);
	}

	@Action(DeleteDocument)
	deleteDocument(
		ctx: StateContext<CrisisTeamStateModel>,
		action: DeleteDocument
	) {
		ctx.dispatch([new StartLoading()]);
		return this.crisisTeamBackend.deleteDocument(action.doc).pipe(
			tap((documents) => {
				ctx.patchState({ documents: documents });
				ctx.dispatch([new StopLoading()]);
			}),
			catchError((error) => {
				ctx.dispatch([new StopLoading()]);
				return throwError(() => error);
			})
		);
	}

	@Action(AddDocument)
	addDocument(ctx: StateContext<CrisisTeamStateModel>, action: AddDocument) {
		ctx.dispatch([new StartLoading()]);
		return this.crisisTeamBackend.addDocument(action.doc).pipe(
			tap((documents) => {
				ctx.patchState({ documents: documents });
				ctx.dispatch([new StopLoading()]);
			}),
			catchError((error) => {
				ctx.dispatch([new StopLoading()]);
				return throwError(() => error);
			})
		);
	}

	@Action(GetTravelStatus)
	getTravelStatus(
		ctx: StateContext<CrisisTeamStateModel>,
		_action: GetTravelStatus
	) {
		ctx.dispatch([new StartLoading()]);
		return this.crisisTeamBackend.getTravelStatus().pipe(
			tap((statuses) => {
				ctx.patchState({
					statuses: statuses,
				});
				ctx.dispatch([new StopLoading()]);
			}),
			catchError((error) => {
				ctx.dispatch([new StopLoading()]);
				return throwError(() => error);
			})
		);
	}

	@Action(GetUserDropDowns)
	getUserDropDowns(
		ctx: StateContext<CrisisTeamStateModel>,
		_action: GetUserDropDowns
	) {
		return this.crisisTeamBackend
			.getUserDropDowns()
			.pipe(
				tap((dropDowns) => ctx.patchState({ userDropDowns: dropDowns }))
			);
	}

	@Action(Logout)
	logout(ctx: StateContext<CrisisTeamStateModel>, _action: Logout) {
		ctx.patchState(DEFAULT_STATE);
	}
}
